import { Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { AttributesPickerStore } from '@apps/mtjr/vendors/app/product/attributes/attributes.store';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonTitle,
  IonToolbar,
  NavParams,
} from '@ionic/angular/standalone';
import { VxErrorBarComponent } from '@libs/core/lib/error-view/error-bar.component';
import { VxTextFieldComponent } from '@libs/core/lib/form-controls/mtjr-text-field.component';
import { Failure, LocalFailure } from '@libs/core/lib/state/failure';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { VxAttachmentFieldComponent } from '@libs/mtjr/shared/attachment/attachment-field.component';
import { productVariationUpdateUtils } from '@libs/mtjr/shared/generated/forms';
import { Entity } from '@libs/mtjr/shared/generated/types';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'mav-variation-editor',
  imports: [
    IonHeader,
    IonTitle,
    IonButtons,
    IonBackButton,
    IonContent,
    IonToolbar,
    IonItem,
    IonButton,
    ReactiveFormsModule,
    FormsModule,
    VxAttachmentFieldComponent,
    VxTextFieldComponent,
    TranslatePipe,
    VxErrorBarComponent,
  ],
  template: `
    <ion-header [translucent]="true">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-back-button
            [disabled]="attachmentField.hasPendingUploads()"
            [text]="'COMMON.BACK' | translate"
          ></ion-back-button>
        </ion-buttons>
        <ion-title>{{ 'PRODUCTS.VARIATION_EDITOR' | translate }}</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="save()">{{ 'COMMON.SAVE' | translate }}</ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <div class="pt-4">
        <form [formGroup]="updateUtils.form">
          <div class="mx-2">
            <ion-item lines="none">
              <div class="size-32 me-4">
                <mtjr-attachment-field
                  #attachmentField
                  [field]="updateUtils.form.controls.image"
                  [singleMode]="true"
                  [showLabel]="false"
                />
              </div>

              @for (attr of variant.attributes; track $index) {
                @if (attr.type === 'color') {
                  <span
                    [style.backgroundColor]="attr.key"
                    class="size-6 inline-block rounded-lg border-solid border-[2px] border-medium"
                  ></span>
                } @else {
                  <span>{{ attr.label }}</span>
                }
                @if ($index < variant.attributes.length - 1) {
                  <span class="mx-1">/</span>
                }
              }
            </ion-item>
          </div>
          <div class="mt-2 px-5 py-3">
            <fieldset>
              <mtjr-text-field
                [field]="updateUtils.form.controls.price"
                [label]="'PRODUCTS.PRICE' | translate"
                type="number"
              />
              <div class="my-4"></div>
              <mtjr-text-field
                [field]="updateUtils.form.controls.stock"
                [label]="'PRODUCTS.PRODUCT_QUANTITY' | translate"
                type="number"
              />
              <div class="my-4"></div>
              <mtjr-text-field
                data-cy="product-discount-input"
                [field]="updateUtils.form.controls.discount"
                label="{{ 'PRODUCTS.DISCOUNT' | translate }}"
                type="number"
                class="w-full"
              />
            </fieldset>
          </div>
        </form>
        <div class="mx-5 mt-2">
          <mtjr-error-bar [failure]="failure()"></mtjr-error-bar>
        </div>
      </div>
    </ion-content>
  `,
})
export class VariationEditorPage {
  navParams = inject(NavParams);
  attributesStore: InstanceType<typeof AttributesPickerStore> = this.navParams.get('attributesStore');
  variant: Entity.ProductVariation = this.navParams.get('variant');
  updateUtils = productVariationUpdateUtils(inject(NonNullableFormBuilder), this.variant);
  elmRef = inject(ElementRef);
  attachmentField = viewChild(VxAttachmentFieldComponent);
  alerts = inject(AppAlerts);
  failure = signal<Failure | null>(null);
  constructor() {
    this.attributesStore.setLastSelectedVariant(this.variant);
  }

  save() {
    const result = this.updateUtils.parse();
    if (!result.success) {
      this.updateUtils.form.markAllAsTouched();
      this.failure.set(new LocalFailure());
      return;
    }

    if (this.attachmentField()?.hasPendingUploads() === true) {
      this.alerts.dangerSnackbarTR('COMMON.ATTACHMENTS_UPLOAD_IN_PROGRESS');
      return;
    }

    const updated = this.attributesStore.updateVariant(this.variant, result.data);
    this.attributesStore.setLastSelectedVariant(updated);
    this.elmRef.nativeElement.closest('ion-nav')?.pop();
  }
}
